import { useEffect, useRef, useState } from 'react'
import type { DocKind, PendingDoc, UploadJobStatus } from '../api'
import { getUploadJob } from '../api'

type DocStatus = {
  kind: DocKind
  jobId: string
  fileName: string
  status: 'pending' | 'running' | 'succeeded' | 'failed'
  count: number | null         // findings_count / encumbrances_count when done
  error: string | null
  elapsed: number              // seconds since this banner mounted
  // Number of pages of an embedded Section 10.7 certificate the
  // contract parser detected + extracted on this upload. Only ever
  // set on contract uploads; surfaces as a small "Also detected a
  // s10.7 inside your contract" annotation on the success row.
  embeddedCertPages: number | null
}

const PRETTY: Record<DocKind, { name: string; verb: string; countLabel: string }> = {
  cert: {
    name: 'Section 10.7 certificate',
    verb: 'Reviewing',
    countLabel: 'findings',
  },
  contract: {
    name: 'Contract of sale',
    verb: 'Reviewing',
    countLabel: 'encumbrances',
  },
}

// Rotating per-elapsed flavour copy. Each second-bucket carries a
// large pool we cycle through every 4 s so the same line never
// lingers too long — and consecutive runs read differently because
// the rotation starts at a random offset per mount.
const FLAVOUR: Array<{ until: number; lines: readonly string[] }> = [
  {
    until: 10,
    lines: [
      'opening the file',
      'reading the cover page',
      'splitting the pages out',
      'checking the letterhead',
      'counting the pages',
      'getting a feel for the layout',
      'looking for the prescribed-matter table',
    ],
  },
  {
    until: 25,
    lines: [
      'extracting clauses',
      'matching prescribed matters',
      'reading the boxed callouts',
      'working through the schedule',
      'reading the contents page',
      'finding the 88B annexure',
      'highlighting the relevant pages',
      'tracking down the explicit CDC line',
    ],
  },
  {
    until: 50,
    lines: [
      'scanned pages OCR one at a time',
      'translating the legalese',
      'cross-checking the council letterhead',
      'reading the 88B annexure',
      'tracing easement strips on the plan',
      'noting each restrictive covenant',
      'capturing positive covenants',
      'reading the special conditions schedule',
      'reading what council said about bushfire',
      'reading what council said about flood',
      'reading the heritage findings',
    ],
  },
  {
    until: 95,
    lines: [
      'tracing covenants back to the lot',
      'still working — multi-page scans take a beat',
      'making sure the easement burdens land on the right title',
      'lining up bushfire / flood findings',
      'cross-checking the lot/DP on the schedule',
      'measuring the easement strip widths',
      'matching the 10.7 to the spatial overlays',
      'reading the contamination notice',
      'reading the road-widening reservations',
      'sorting findings by prescribed-matter item',
    ],
  },
  {
    until: 180,
    lines: [
      'almost there — heavy scans can push to a few minutes',
      'the document has more pages than usual',
      'patience — careful is better than fast',
      'still on it — the 88B is wordy',
      'we promise we\'re reading every page',
      'the appendix is a long one',
      'no shortcuts on a 10.7',
    ],
  },
  {
    until: 9_999,
    lines: [
      'still working — scanned 88Bs are slow but solvable',
      'careful is better than fast',
      'reading the schedule for the third time',
      'this one\'s a chunky read',
      'making sure no covenant slips through',
    ],
  },
]

function flavourLine(elapsed: number, offset: number): string {
  const bucket = FLAVOUR.find((b) => elapsed < b.until) ?? FLAVOUR[FLAVOUR.length - 1]
  const lines = bucket.lines
  // Rotate within the bucket every 4 s, but start at a per-mount
  // random offset so the same line never opens two consecutive runs.
  const idx = (Math.floor(elapsed / 4) + offset) % lines.length
  return lines[idx]
}

export function DocHydrationBanner({
  pending,
  onAllDone,
}: {
  pending: PendingDoc[]
  onAllDone: () => void
}) {
  // Per-mount random offset so the flavour rotation starts fresh
  // each visit (and per-doc within a visit if both cert + contract
  // are in flight at the same time).
  const offsetRef = useRef<Record<string, number>>({})
  if (Object.keys(offsetRef.current).length === 0) {
    pending.forEach((p) => {
      offsetRef.current[p.jobId] = Math.floor(Math.random() * 1000)
    })
  }
  const [statuses, setStatuses] = useState<DocStatus[]>(() =>
    pending.map((p) => ({
      kind: p.kind,
      jobId: p.jobId,
      fileName: p.fileName,
      status: 'pending',
      count: null,
      error: null,
      elapsed: 0,
      embeddedCertPages: null,
    })),
  )

  // Tick the elapsed counter every second for live patience copy.
  useEffect(() => {
    const startedAt = Date.now()
    const t = setInterval(() => {
      setStatuses((prev) => prev.map((s) =>
        s.status === 'succeeded' || s.status === 'failed'
          ? s
          : { ...s, elapsed: Math.round((Date.now() - startedAt) / 1000) },
      ))
    }, 1000)
    return () => clearInterval(t)
  }, [])

  // Per-job poll loop. One independent timer per job so a 90s cert
  // parse doesn't keep the contract banner stuck on "pending".
  useEffect(() => {
    let cancelled = false
    const handles: Array<ReturnType<typeof setTimeout>> = []

    function scheduleNext(jobId: string, delayMs: number) {
      const h = setTimeout(() => poll(jobId), delayMs)
      handles.push(h)
    }

    async function poll(jobId: string) {
      if (cancelled) return
      let next: UploadJobStatus | null = null
      try {
        next = await getUploadJob(jobId)
      } catch {
        scheduleNext(jobId, 4000)
        return
      }
      if (cancelled || !next) return
      setStatuses((prev) => prev.map((s) => {
        if (s.jobId !== jobId) return s
        const r = next!.result ?? {}
        const count = typeof r.findings_count === 'number'
          ? r.findings_count
          : typeof r.encumbrances_count === 'number'
          ? r.encumbrances_count
          : null
        const embeddedCertPages =
          typeof r.embedded_cert_pages === 'number'
            ? r.embedded_cert_pages
            : null
        return {
          ...s,
          status: next!.status,
          count,
          embeddedCertPages,
          error: next!.error,
        }
      }))
      if (next.status === 'pending' || next.status === 'running') {
        scheduleNext(jobId, 3000)
      }
    }

    pending.forEach((p) => poll(p.jobId))
    return () => {
      cancelled = true
      handles.forEach((h) => clearTimeout(h))
    }
  }, [pending])

  // When every job has reached a terminal state, hand control back to
  // the parent so it can re-fetch the feasibility report.
  const settled = statuses.every((s) => s.status === 'succeeded' || s.status === 'failed')
  useEffect(() => {
    if (settled) onAllDone()
  }, [settled, onAllDone])

  if (pending.length === 0) return null

  const anyRunning = statuses.some((s) => s.status === 'pending' || s.status === 'running')

  // Split the queue: in-progress docs deserve the featured spinner
  // card so the planner can't miss that a review is still happening,
  // while settled rows (verified / failed) stay compact below.
  const pendingStatuses = statuses.filter((s) => s.status === 'pending' || s.status === 'running')
  const settledStatuses = statuses.filter((s) => s.status === 'succeeded' || s.status === 'failed')
  const allSettled = pendingStatuses.length === 0 && settledStatuses.length > 0
  const anySucceeded = settledStatuses.some((s) => s.status === 'succeeded')
  const anyFailed = settledStatuses.some((s) => s.status === 'failed')

  // When every doc has finished the banner switches into a deliberate
  // success state — big check, a "Review complete" headline, and a
  // line spelling out exactly which sections of the report just
  // updated so the planner KNOWS the work landed. App auto-dismisses
  // the banner ~6 s later.
  if (allSettled) {
    const tone = anyFailed ? (anySucceeded ? 'mixed' : 'failed') : 'success'
    const ring =
      tone === 'failed' ? 'border-flag-red/45' :
      tone === 'mixed' ? 'border-flag-amber/45' :
      'border-flag-green/55'
    const headline =
      tone === 'failed' ? 'Review failed' :
      tone === 'mixed' ? 'Review complete — with issues' :
      'Review complete'
    const sub =
      tone === 'failed' ? 'No findings could be ingested. Section B falls back to spatial-layer estimates.' :
      tone === 'mixed' ? 'Some documents reviewed; others failed. The report has been refreshed with what we got.' :
      'Council-issued findings have been applied to Section B, Section I and the CDC verdict above.'
    return (
      <div className={`mt-6 relative overflow-hidden rounded-lg glass border ${ring}`}>
        <div className="relative px-6 py-5">
          <div className="flex items-start gap-4">
            <CompleteMark tone={tone} />
            <div className="flex-1 min-w-0">
              <div className="brand-eyebrow text-massing-ink-light mb-1">Documents</div>
              <div className="font-serif text-2xl text-massing-ink leading-tight">{headline}</div>
              <p className="text-[13px] text-massing-ink-mid mt-1.5 leading-snug max-w-2xl">{sub}</p>
            </div>
          </div>
          <ul className="mt-4 space-y-2 pl-1">
            {settledStatuses.map((s) => (
              <SettledDocRow key={s.jobId} status={s} />
            ))}
          </ul>
        </div>
      </div>
    )
  }

  return (
    <div className="mt-6 relative overflow-hidden rounded-lg glass border border-massing-highlight/30">
      {anyRunning && (
        <div className="absolute inset-0 pointer-events-none opacity-60">
          <div
            className="absolute inset-y-0 w-1/2 brand-gradient-bg blur-3xl opacity-25"
            style={{ animation: 'shimmer-sweep 5.2s ease-in-out infinite' }}
          />
        </div>
      )}
      <div className="relative px-6 py-5">
        <div className="brand-eyebrow text-massing-ink-light mb-3">
          Documents under review
        </div>

        {pendingStatuses.length > 0 && (
          <div className="space-y-4">
            {pendingStatuses.map((s) => (
              <PendingDocCard
                key={s.jobId}
                status={s}
                offset={offsetRef.current[s.jobId] ?? 0}
              />
            ))}
          </div>
        )}

        {settledStatuses.length > 0 && (
          <ul className={`space-y-2.5 ${pendingStatuses.length > 0 ? 'mt-4 pt-4 border-t border-massing-rule-soft' : ''}`}>
            {settledStatuses.map((s) => (
              <SettledDocRow key={s.jobId} status={s} />
            ))}
          </ul>
        )}

        {anyRunning && (
          <p className="mt-4 text-[11.5px] text-massing-ink-light italic leading-snug">
            Section B overlays will update automatically — no need to refresh.
          </p>
        )}
      </div>
    </div>
  )
}

function CompleteMark({ tone }: { tone: 'success' | 'mixed' | 'failed' }) {
  const color =
    tone === 'failed' ? 'bg-flag-red' :
    tone === 'mixed' ? 'bg-flag-amber' :
    'bg-flag-green'
  const glyph = tone === 'failed' ? '✕' : tone === 'mixed' ? '!' : '✓'
  return (
    <span className="relative shrink-0">
      <span
        className={`block w-12 h-12 rounded-full ${color} text-[#0a0c1a] font-bold text-2xl flex items-center justify-center`}
        aria-hidden
      >
        {glyph}
      </span>
      <span
        className={`absolute inset-0 rounded-full ${color} opacity-30 blur-md -z-10`}
        aria-hidden
      />
    </span>
  )
}

function PendingDocCard({ status, offset }: { status: DocStatus; offset: number }) {
  const meta = PRETTY[status.kind]
  // Format the elapsed counter so it reads as a stopwatch rather
  // than a small number — long reviews (120-200 s) feel handled.
  const mm = Math.floor(status.elapsed / 60).toString().padStart(2, '0')
  const ss = (status.elapsed % 60).toString().padStart(2, '0')
  return (
    <div className="relative flex items-center gap-5 rounded-md surface-shade border border-massing-rule-soft px-4 py-4">
      {/* Iridescent ring spinner — the focal visual. Sized so it
          dominates the row but doesn't tower over the header above. */}
      <div className="brand-spinner" style={{ width: 56, height: 56 }} aria-hidden />
      <div className="flex-1 min-w-0">
        <div className="text-massing-ink font-medium text-[15px] leading-tight">
          {meta.name}
        </div>
        <div className="text-[12px] text-massing-ink-light truncate">
          {status.fileName}
        </div>
        <div className="mt-1.5 text-[12.5px] text-massing-ink-mid italic">
          {meta.verb} · <span className="brand-gradient-text font-medium not-italic">{flavourLine(status.elapsed, offset)}</span>
        </div>
      </div>
      <div className="text-right">
        <div className="font-mono text-[18px] tracking-tight text-massing-ink tabular-nums">
          {mm}:{ss}
        </div>
        <div className="brand-eyebrow text-massing-ink-light/80 text-[9px]">elapsed</div>
      </div>
    </div>
  )
}

function SettledDocRow({ status }: { status: DocStatus }) {
  const meta = PRETTY[status.kind]
  if (status.status === 'succeeded') {
    return (
      <li className="flex items-start gap-3 text-sm">
        <StatusDot tone="green" />
        <div className="flex-1">
          <div className="text-massing-ink">
            <span className="font-medium">{meta.name}</span>
            <span className="text-massing-ink-light"> · {status.fileName}</span>
          </div>
          <div className="text-[12px] text-massing-ink-mid">
            verified
            {status.count != null && ` · ${status.count} ${meta.countLabel} ingested`}
            {` · ${status.elapsed}s`}
          </div>
          {/* NSW contracts of sale routinely bundle the s10.7 into
              the same PDF; if we extracted + parsed it, tell the
              planner so they understand the report now covers both
              halves from a single upload. */}
          {status.kind === 'contract' && (status.embeddedCertPages ?? 0) > 0 && (
            <div className="text-[12px] text-flag-green mt-0.5">
              Also detected a Section 10.7 certificate inside this contract
              ({status.embeddedCertPages} pages) — parsed and folded into
              the report.
            </div>
          )}
        </div>
      </li>
    )
  }
  // failed
  return (
    <li className="flex items-start gap-3 text-sm">
      <StatusDot tone="red" />
      <div className="flex-1">
        <div className="text-massing-ink">
          <span className="font-medium">{meta.name}</span>
          <span className="text-massing-ink-light"> · {status.fileName}</span>
        </div>
        <div className="text-[12px] text-massing-maroon italic">
          review failed{status.error ? ` — ${status.error}` : ''}. Section B falls back to spatial-layer estimates.
        </div>
      </div>
    </li>
  )
}

function StatusDot({ tone }: { tone: 'green' | 'red' }) {
  const color = tone === 'green' ? 'bg-flag-green' : 'bg-flag-red'
  return (
    <span
      className={`inline-block w-2 h-2 mt-1.5 rounded-full ${color}`}
      aria-hidden
    />
  )
}
